/**
 * Локали продукта: какие есть, куда пишутся, каким тегом разговаривает `Intl`.
 *
 * Файл нарочно крошечный и без зависимостей: его читают обе сборки, приёмка
 * каталогов и любой скрипт, которому нужно знать список локалей. Всё, что
 * сложнее таблицы, живёт рядом — в `runtime.ts` (строки) и `../intl.ts`
 * (числа и даты).
 *
 * Локалей ровно три, и они не одно и то же:
 *
 * * `ru` — РФ-продукт, **дефолт всего репозитория**. Никакой код не имеет права
 *   вести себя иначе, пока локаль не выбрана явно: РФ-кабинет работает сегодня,
 *   и B1 не должен этого заметить;
 * * `en` — глобальный продукт, рынок ОАЭ (план глобала, этап C: первая
 *   вертикаль — недвижимость Дубая);
 * * `ar` — тот же рынок по-арабски, письмо справа налево и шесть форм
 *   множественного числа.
 */

export const LOCALES = ['ru', 'en', 'ar'] as const;

export type Locale = (typeof LOCALES)[number];

export type Direction = 'ltr' | 'rtl';

/** Направление письма. Уезжает в `<html dir>` и в логические свойства (B4). */
export const DIRECTION: Record<Locale, Direction> = {
  ru: 'ltr',
  en: 'ltr',
  ar: 'rtl',
};

/**
 * Тег BCP-47, которым разговаривает `Intl`. Не то же самое, что код локали:
 * `Intl` считает по региону, а не по языку.
 *
 * `ar` записан с расширением `-u-nu-latn`, и это не украшение. Система
 * счисления у арабского **зависит от региона**: `ar-EG` печатает 9 900 как
 * ٩٬٩٠٠, `ar-AE` сегодня печатает 9,900. То есть западные цифры в ОАЭ мы
 * получаем не по своему решению, а по нынешней таблице CLDR — и потеряем их
 * молча, если сменится регион рынка (`ar-SA`, `ar-EG`) или сама таблица.
 *
 * Дизайн-система (B3, `data-viz.md`) требует западных цифр во всех показаниях
 * продукта на всех рынках: показание — это число, которое клиент сверяет с
 * выгрузкой, со счётом и со снимком чужого кабинета. Такое требование не
 * оставляют на усмотрение чужой таблицы, поэтому запись цифр названа здесь
 * явно.
 *
 * Расширение стоит **в теге**, а не в `options.numberingSystem`, по двум
 * причинам: тег действует одинаково на `NumberFormat` и на `DateTimeFormat`, и
 * его видно в одном месте — в этой таблице. Приёмка `tools/check-i18n.mjs`
 * проверяет, что оно отсюда не пропало.
 */
export const LOCALE_TAG: Record<Locale, string> = {
  ru: 'ru-RU',
  en: 'en-AE',
  ar: 'ar-AE-u-nu-latn',
};

/** Название языка на нём самом — для переключателя языка (B2/B5). */
export const LOCALE_NAME: Record<Locale, string> = {
  ru: 'Русский',
  en: 'English',
  ar: 'العربية',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Разобрать то, что пришло снаружи: поле `locale` учётной записи, значение из
 * хранилища браузера, кусок пути лендинга, `navigator.language`.
 *
 * Берётся только первая часть тега (`en-GB` → `en`) и только из известных:
 * неизвестное значение возвращает `null`, а решение, чем его заменить,
 * принимает вызывающий — у кабинета и у лендинга ответы разные.
 */
export function parseLocale(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const base = value.trim().toLowerCase().split(/[-_]/)[0];
  return isLocale(base) ? base : null;
}

/**
 * Язык страницы витрины — из адреса, а не из настройки.
 *
 * У лендинга и у кабинета разные ответы на вопрос «на каком языке показывать»,
 * и это не разнобой. Кабинет читает вошедший человек, у него есть учётная
 * запись и в ней поле языка. Витрину читают покупатель, краулер и конвейер
 * PDF — у последних двух нет ни записи, ни памяти между заходами, а адрес есть
 * всегда. Язык, спрятанный в куке или в хранилище, для них означает одно:
 * поисковик проиндексирует одну версию страницы из трёх, а `hreflang`
 * окажется обещанием, которого страница не выполняет.
 *
 * Корень без префикса — язык рынка (`/` в РФ русская). Маршруты `/en/` и
 * `/ar/` появляются в B2; функция стоит здесь заранее, потому что разбирать
 * адрес обязаны одинаково и страница, и `hreflang`, и карта сайта.
 */
export function pathLocale(pathname: string): Locale | null {
  const first = pathname.replace(/^\/+/, '').split('/')[0];
  return parseLocale(first);
}
